import React, { useState } from 'react';
import { Box, Dialog, Stack, Typography } from '@mui/material';

import { Button } from '../../../components/core';
import { useDeleteApiKeyMutation } from '../../../store/modules/merchant/api';
import { toast } from '../../../util/toast';
import { ApiKeyInfo } from '../components/ApiKeyInfo';
import { ApiKeyResponse } from '../../../store/modules/merchant/types';

interface Props {
  open: boolean;
  onClose: () => void;
  apiKey: ApiKeyResponse;
  displayKey: string;
}

const ApiKeyDeleteModal = ({ open, onClose, apiKey, displayKey }: Props) => {
  const { apiKeyId, merchantId } = apiKey;

  const [deleteApiKey] = useDeleteApiKeyMutation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteApiKey = async () => {
    setIsDeleting(true);
    await deleteApiKey({ merchantId, apiKeyId })
      .unwrap()
      .then(() => {
        onClose();
        toast.success('API Key has been deleted');
        setIsDeleting(false);
      })
      .catch(() => {
        toast.error('An error occurred while deleting the API key. Please try again later.');
        setIsDeleting(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
          minWidth: { xs: '90%', md: '733px' },
          p: 3,
        },
      }}
    >
      <Typography variant="xl" fontWeight="semibold" sx={{ mb: 3 }}>
        Are you sure you want to delete this API key?
      </Typography>

      <Typography variant="large" fontWeight="semibold" sx={{ mb: 3 }}>
        This API key will be removed and no longer active upon deletion.
      </Typography>

      <Box sx={{ mb: 6 }}>
        <ApiKeyInfo apiKey={apiKey} displayKey={displayKey} />
      </Box>
      <Stack spacing={2} sx={{ '& button': { height: '48px' } }}>
        <Button
          text={isDeleting ? 'Deleting...' : 'Delete API Key'}
          disabled={isDeleting}
          onClick={handleDeleteApiKey}
          theme="primary"
        />
        <Button text="Cancel" disabled={isDeleting} onClick={onClose} theme="secondary" />
      </Stack>
    </Dialog>
  );
};

export default ApiKeyDeleteModal;
